import React, { useCallback, useContext, useEffect, useState } from "react";
import classNames from "classnames";
import Pagination from "../../../utils/components/common/pagination";
import { formatDateTime, valueFinderByModes } from "../../../utils/utils";
import Icons from "../../../utils/components/icons";
import { UserContext } from "../../../layouts/MainLayout";
import { getDeviceTelemetry } from "../../../services/DeviceService";

import "./listsView.scss";
import {
  DEVICE_MODES,
  getModeValue,
} from "../../../utils/constants/deviceConstants";

const ListsView = ({ deviceUUID, updateListView, updateTelemetryData }) => {
  const [columList, setColumList] = useState([
    { key: "clo2Value", label: "PPB", val: "", clickCount: 0 },
    { key: "mode", label: "Mode", val: "", clickCount: 0 },
    { key: "cartridgeLevel", label: "Cartridge (%)", val: "", clickCount: 0 },
    { key: "temperature", label: "Temp (\u00b0C)", val: "", clickCount: 0 },
    { key: "humidity", label: "Humidity (%)", val: "", clickCount: 0 },
    { key: "timeStamp", label: "Timestamp", val: "", clickCount: 0 },
  ]);

  const { userData } = useContext(UserContext);

  const itemPerPage = 10;
  const [dataList, setDataList] = useState([]);
  const [pageCount, setPageCount] = useState(1);
  const [pageTotalCount, setPageTotalCount] = useState();
  const [isLoading, setIsLoading] = useState(true);

  const sortLabel = {
    asc: "Ascending sort",
    desc: "Descending sort",
    "": "No sort",
  };

  useEffect(() => {
    updateTelemetryData(dataList);
  }, [dataList, updateTelemetryData]);

  const subtractHours = useCallback((date, hours) => {
    date.setHours(date.getHours() - hours);
    return date;
  }, []);

  const handlePageChange = useCallback((item) => {
    setPageCount(item + 1);
  }, []);

  const getDeviceTelemetryData = useCallback(() => {
    const queryParam = {
      deviceId: deviceUUID,
      tenantId: userData?.OrganizationID,
      size: itemPerPage,
      pageNo: pageCount,
      filters: {
        eventDate: {
          between: [subtractHours(new Date(), 1), new Date()],
        },
      },
      sort: [
        {
          eventDate:
            columList.find((data) => data.key === "timeStamp").clickCount === 1
              ? "ASC"
              : "DESC",
        },
      ],
    };

    getDeviceTelemetry(queryParam)
      .then((resp) => {
        setIsLoading(false);
        if (resp?.data?.Items) {
          const data = resp.data.Items.map((data) => {
            return { ...data, mode: getModeValue(data?.mode) };
          });
          setDataList(data);
          setPageTotalCount(
            Math.ceil(resp.data.TotalTelemetryCount / itemPerPage)
          );
        }
      })
      .catch((err) => {
        setIsLoading(false);
        // setDataList([]);
      });
  }, [
    deviceUUID,
    userData?.OrganizationID,
    pageCount,
    subtractHours,
    columList,
  ]);

  useEffect(() => {
    getDeviceTelemetryData();
  }, [
    pageCount,
    deviceUUID,
    userData?.OrganizationID,
    getDeviceTelemetryData,
    columList,
    updateListView,
  ]);

  const applySorting = (key, val) => {
    let sortColumnIndex = columList.findIndex((item) => item.key === key);
    let listCopy = [...columList];

    if (listCopy[sortColumnIndex].clickCount === 2) {
      listCopy[sortColumnIndex].clickCount = 0;
      listCopy[sortColumnIndex].val = "";
    } else {
      listCopy[sortColumnIndex].clickCount =
        listCopy[sortColumnIndex].clickCount + 1;
      listCopy[sortColumnIndex].val =
        listCopy[sortColumnIndex].clickCount === 1 ? "asc" : "desc";
    }
    setColumList(listCopy);
  };

  const modeLabels = {
    OFFLINE: "Offline",
    SENSE_ONLY: "Sense Only",
    STANDBY: "Standby",
    OPERATIONAL: "Operational",
  };

  return (
    <div>
      {dataList.length && !isLoading ? (
        <div className="listViewWrapper">
          <div className="deviceTitle heading" id="displayedDuration">
            Last 60 Minutes
          </div>
          <div className="listViewInner">
            {dataList.length && !isLoading ? (
              <table className="table table-striped theme-table">
                <thead>
                  <tr>
                    <th>&nbsp;</th>
                    {columList.map((item, index) => (
                      <th
                        onClick={() => applySorting(item.key)}
                        key={`device_list_column_${index}`}
                        className="deviceListView"
                      >
                        <div className="sortIconWrapper">
                          <span
                            className="clipText"
                            title={item.label}
                            id={`${item.label}Column`}
                          >
                            {item.label}
                          </span>
                          {item.key === "timeStamp" && (
                            <div
                              className="sortIcons"
                              title={sortLabel[item.val]}
                            >
                              <button
                                type="button"
                                className={classNames({
                                  active: item.val === "asc",
                                })}
                                id="upArrowBtn"
                              >
                                {Icons.upArrowIcon}
                              </button>
                              <button
                                type="button"
                                className={classNames({
                                  active: item.val === "desc",
                                })}
                                id="downArrowBtn"
                              >
                                {Icons.downArrowIcon}
                              </button>
                            </div>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dataList.map((item, index) => (
                    <tr
                      key={`device_view_list_${index}`}
                      className="deviceListView"
                    >
                      <td></td>
                      <td title={`Device PPB: ${item.clo2Value}`}>
                        <div className="clipText">
                          {valueFinderByModes(
                            item?.mode,
                            "ppb",
                            item?.clo2Value,
                            false
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="clipText" name="modeValues">
                          {item?.mode && DEVICE_MODES[item?.mode].label}
                        </div>
                      </td>
                      <td>
                        <div className="clipText">
                          {valueFinderByModes(
                            item?.mode,
                            "cartridge",
                            item?.cartridgeLevel,
                            false
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="clipText" name="temperatureValues">
                          {valueFinderByModes(
                            item?.mode,
                            "temperature",
                            item?.temperature,
                            false
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="clipText" name="humidityValues">
                          {valueFinderByModes(
                            item?.mode,
                            "humidity",
                            item?.humidity,
                            false
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="clipText" name="timeStampValues">
                          {item?.timeStamp
                            ? formatDateTime(new Date(item.timeStamp))
                            : "---"}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="card graphWrapper mt-4">
                <div className="d-flex justify-content-center fluid">
                  <span className="deviceTitle">No Data Received</span>
                </div>
              </div>
            )}
          </div>
          <div className="bodyFooter pagination">
            <Pagination
              currentPage={pageCount - 1}
              totalPages={pageTotalCount}
              pageCallBack={handlePageChange}
            />
          </div>
        </div>
      ) : (
        <></>
      )}
      {!dataList.length && !isLoading ? (
        <div className="d-flex justify-content-center fluid">
          <span className="deviceTitle">No Data Received</span>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};
export default ListsView;
